import * as _ from 'lodash';
import { Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, merge, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, map, mergeMap, shareReplay, startWith, withLatestFrom } from 'rxjs/operators';

import { Item } from 'fc-ui-components';

import { OrderProductName, Searcher } from './searcher';
import { ApiModel, Branch } from 'models';
import { GetByBrandAndStyleInputParams } from './searcher.types';
import { MatrixData, UiMapper } from './ui-mapper';
import { Validator } from './validator';
import { HistoricalOrderArticle } from 'app/order-management/services/order-history/searcher.types';
import { PlaceholderVariant, Row, UpdatedMatrixRow } from 'app/b2b-order/components/matrix/types';
import { WindowService } from 'services';
import { LanguageService, TranslationMap } from 'services/language.service';
import { OrderSheetItem } from '../order-sheet/types';
import { UserService } from 'services';
import { getMatrixSelectedAmount } from 'app/b2b-order/helpers/order';
import { ReorderArticle, ReorderArticleSize, ReorderPrepack } from 'app/b2b-order/types/reorder';
import { PreorderArticle, PreorderArticleSize, PrepackForPreorder } from 'app/b2b-order/types/preorder';
import { OrderArticle, OrderArticleColor } from 'app/b2b-order/types/order';
import { FcProductName } from 'types';
import { ShoppingCartItem } from '../shopping-cart/types';

@Injectable()
export class OrderProductDetailService {
    private searcher: Searcher;
    private uiMapper: UiMapper;
    private validator: Validator;

    selectedQuantityMap: { [gtin: string]: number } = {};

    constructor(
        apiModel: ApiModel,
        private windowService: WindowService,
        private languageService: LanguageService,
        private userService: UserService
    ) {
        this.searcher = new Searcher(apiModel);
        this.uiMapper = new UiMapper();
        this.validator = new Validator();
    }

    public getArticleByBrandAndStyleForReorder({
        brandId,
        styleGroupId,
        language,
        retailerId
    }: GetByBrandAndStyleInputParams): Observable<{
        articles: ReorderArticle[];
        colors: OrderArticleColor[];
        hasExclusivityAccess: boolean;
        hasStoreStock: boolean;
    }> {
        return this.searcher.getArticleByBrandAndStyleForReorder({ brandId, styleGroupId, language, retailerId });
    }

    public getArticleByBrandAndStyleForPreorder({
        brandId,
        styleGroupId,
        language,
        retailerId
    }: GetByBrandAndStyleInputParams): Observable<{
        articles: PreorderArticle[];
        colors: OrderArticleColor[];
        hasExclusivityAccess: boolean;
    }> {
        return this.searcher.getArticleByBrandAndStyleForPreorder({ brandId, styleGroupId, language, retailerId });
    }

    public mapArticleColorToItem({ code, name, colorGroupId, key, hasStock }: OrderArticleColor) {
        return this.uiMapper.mapArticleColorToItem({ code, name, colorGroupId, key, hasStock });
    }

    public noArticleSelectedForOrder(articles: ReorderArticle[] | PreorderArticle[]): boolean {
        return this.validator.noArticleSelectedForOrder(articles);
    }

    public allArticleAmountsValid(articles: ReorderArticle[] | PreorderArticle[]): boolean {
        return this.validator.allArticleAmountsValid(articles);
    }

    public historyArticleToMatrixSizeRowMapper(article: HistoricalOrderArticle) {
        return this.uiMapper.historyArticleToMatrixSizeRowMapper(article);
    }

    public mapArticleToMatrix(
        article: ReorderArticle,
        config?: ArticleMapConfig
    ): Partial<
        (OrderArticle | ReorderArticle) & {
            options: {
                showTotals: boolean;
                placeholderVariant?: PlaceholderVariant;
            };
            dataSource: Row[];
            matrixData: MatrixData;
            isNewMatrixEnabled: boolean;
        }
    > {
        if (config.isNewMatrixEnabled) {
            const newMatrixData = this.uiMapper.mapArticleToMatrixData(article, {
                ...config,
                productType: OrderProduct.Reorder,
                userLanguage: this.userService.getPreferredLanguage(),
                selectedQuantityMap: this.selectedQuantityMap
            });

            let legacyMatrixData: ReorderArticle & {
                options: {
                    showTotals?: boolean;
                    placeholderVariant?: PlaceholderVariant;
                };
                dataSource: Row[];
            } = null;

            if (!config.isShoppingCart) {
                legacyMatrixData = this.uiMapper.mapArticleToMatrix(
                    article,
                    config?.prepackLabelPrefix,
                    config?.placeholderVariant
                );
            }

            return {
                ...legacyMatrixData,
                ...newMatrixData,
                isNewMatrixEnabled: config?.isNewMatrixEnabled && Boolean(newMatrixData.matrixData)
            };
        }

        return {
            ...this.uiMapper.mapArticleToMatrix(article, config?.prepackLabelPrefix, config?.placeholderVariant),
            isNewMatrixEnabled: false
        };
    }

    public mapPreorderArticleToMatrix(
        article: PreorderArticle,
        config?: ArticleMapConfig
    ): Partial<
        PreorderArticle & {
            options: {
                showTotals: boolean;
                placeholderVariant?: PlaceholderVariant;
            };
            matrixData: MatrixData;
            isNewMatrixEnabled?: boolean;
        }
    > {
        if (config.isNewMatrixEnabled) {
            const newMatrixData = this.uiMapper.mapArticleToMatrixData(article, {
                ...config,
                productType: OrderProduct.Preorder,
                userLanguage: this.userService.getPreferredLanguage()
            });

            return {
                ...newMatrixData,
                ...this.uiMapper.mapPreorderArticleToMatrix(
                    article,
                    config?.prepackLabelPrefix,
                    config?.placeholderVariant
                ),
                isNewMatrixEnabled: config?.isNewMatrixEnabled && Boolean(newMatrixData.matrixData)
            };
        }
        return this.uiMapper.mapPreorderArticleToMatrix(
            article,
            config?.prepackLabelPrefix,
            config?.placeholderVariant
        );
    }

    public mapUpdatedArticleRow(article: ReorderArticle, updatedRow: UpdatedMatrixRow) {
        return this.uiMapper.mapUpdatedArticleRow(article, updatedRow);
    }

    public mapUpdatedPreorderArticleRow(article: PreorderArticle, updatedRow: UpdatedMatrixRow) {
        return this.uiMapper.mapUpdatedPreorderArticleRow(article, updatedRow);
    }

    public mergeArticlesObservableWithSubject<T>(
        articles: Observable<OrderArticlesWithColors>,
        articlesDataSourceChange: Subject<OrderArticles>,
        itemsObservable: Observable<OrderArticles>
    ): Observable<T> {
        return merge(
            itemsObservable.pipe(
                mergeMap(() =>
                    articles.pipe(
                        withLatestFrom(itemsObservable),
                        filter(([{ articles }]) => !_.isEmpty(articles)),
                        map(([{ articles }, items]) => this.getArticleWithAmounts(articles, items))
                    )
                )
            ), // inserted because need to recalculate on post store selection transformation
            articlesDataSourceChange.pipe(
                // articlesDataSourceChange is updated by the `rowValueChanges` matrix events in the components that
                // make use of this method.
                // An issue occurs here where the articlesDataSourceChange subject emits a value twice because the
                // `rowValueChanges` event is emitted twice.
                // As a bypass, debounceTime is used to only take the last value in a short span of time.
                debounceTime(100)
            )
        ).pipe(shareReplay()) as Observable<T>;
    }

    public mapArticleColorsToItemsObservable(articles: Observable<OrderArticlesWithColors>) {
        return articles.pipe(
            map(({ colors }) => _.map(colors, (color: OrderArticleColor) => this.mapArticleColorToItem(color))),
            shareReplay()
        );
    }

    public mapSelectedColorObservable(colorItems: Observable<Item[]>): Observable<Item<string>> {
        return colorItems.pipe(
            map((colorItems) => {
                const urlSearchParams = new URLSearchParams(this.windowService.nativeWindow.location.search);
                const availableColors = colorItems.map((colorItem) => colorItem.value);
                const selectedColorId = urlSearchParams.get('color');

                if (selectedColorId && availableColors.includes(selectedColorId)) {
                    return _.find(colorItems, { value: selectedColorId });
                } else {
                    console.warn('No color selected or selected color is not available');
                    return colorItems[0];
                }
            })
        );
    }

    public mapArticleForOrderHistory(
        article: HistoricalOrderArticle,
        type: OrderProduct
    ): Observable<
        Partial<
            (PreorderArticle | ReorderArticle | OrderArticle) & {
                options: {
                    showTotals: boolean;
                    placeholderVariant?: PlaceholderVariant;
                };
                matrixData: MatrixData;
                dataSource?: Row[];
                isNewMatrixEnabled?: boolean;
            }
        >
    > {
        return this.languageService.translateObservable(Object.values(MatrixLabels)).pipe(
            map((translationsMap) => {
                if (type === OrderProduct.Reorder) {
                    return this.mapArticleToMatrix({ ...article, hasStock: undefined } as ReorderArticle, {
                        isNewMatrixEnabled: true,
                        prepackLabelPrefix: translationsMap[MatrixLabels.Prepack],
                        placeholderVariant: undefined,
                        showAvailableOnly: true,
                        isShoppingCart: true,
                        stores: [],
                        labels: this.generateLabels(translationsMap)
                    });
                }
                return this.mapPreorderArticleToMatrix(article as PreorderArticle, {
                    isNewMatrixEnabled: true,
                    prepackLabelPrefix: translationsMap[MatrixLabels.Prepack],
                    placeholderVariant: PlaceholderVariant.Fixed,
                    isShoppingCart: true,
                    labels: this.generateLabels(translationsMap)
                });
            })
        );
    }

    public mapArticlesToMatrixWithTranslationObservable(
        articles,
        type: OrderProduct,
        showAvailableOnly: BehaviorSubject<boolean> = new BehaviorSubject(false),
        selectedStores: Observable<Branch[]> = of([]),
        showStoreStock: Observable<boolean> = of(false)
    ) {
        return combineLatest([
            articles,
            this.languageService.translateObservable(Object.values(MatrixLabels)),
            showAvailableOnly,
            selectedStores,
            showStoreStock
        ]).pipe(
            map((chain) => {
                const [articles, translationsMap, showAvailableOnly, selectedStores, showStoreStock] =
                    chain as unknown as MatrixObservableChain;
                return articles.reduce(
                    (articlesMatrixDataMap, article) => ({
                        ...articlesMatrixDataMap,
                        [article.color.colorGroupId]:
                            type === OrderProduct.Reorder
                                ? this.mapArticleToMatrix(article as ReorderArticle, {
                                      isNewMatrixEnabled: true,
                                      prepackLabelPrefix: translationsMap[MatrixLabels.Prepack],
                                      placeholderVariant: undefined,
                                      showAvailableOnly,
                                      stores: selectedStores || [],
                                      showStoreStock,
                                      labels: this.generateLabels(translationsMap)
                                  })
                                : this.mapPreorderArticleToMatrix(article as PreorderArticle, {
                                      isNewMatrixEnabled: true,
                                      prepackLabelPrefix: translationsMap[MatrixLabels.Prepack],
                                      placeholderVariant: PlaceholderVariant.Fixed,
                                      labels: this.generateLabels(translationsMap)
                                  })
                    }),
                    {}
                );
            }),
            startWith({}),
            shareReplay()
        );
    }

    public generateLabels(translationsMap: TranslationMap) {
        const labels: { [key in MatrixLabels]: string } = {} as ArticleMapConfig['labels'];

        Object.values(MatrixLabels).forEach((label) => {
            labels[label] = translationsMap[label];
        });

        return labels;
    }

    public populateSelectedQuantityMap(items: ShoppingCartItem[]) {
        const selectedQuantityMap = {};

        items.forEach((item) => {
            item.sizes.forEach((size) => {
                selectedQuantityMap[size.gtin] = getMatrixSelectedAmount(size.stock.selectedAmount);
            });

            item.prepacks.forEach((prepack) => {
                selectedQuantityMap[prepack._id] = getMatrixSelectedAmount(prepack.stock.selectedAmount);
            });
        });

        this.selectedQuantityMap = selectedQuantityMap;
    }

    public getRelatedArticlesByBrand(
        brandId: string,
        retailerId: string,
        styleGroupId: string,
        colorGroupId: string,
        platform: OrderProductName = FcProductName.OrderPlatform
    ) {
        return this.searcher.getRelatedArticleByBrand(brandId, retailerId, styleGroupId, colorGroupId, platform);
    }

    private getArticleWithAmounts(articles: OrderArticles, items: OrderArticles) {
        const sizes = _.flatMap(items, ({ sizes }: ShoppingCartItem | OrderSheetItem) => sizes);
        const prepacks = _.flatMap(items, ({ prepacks }: ShoppingCartItem) => prepacks);

        const getSizeAmountFromCart = (gtin: string) => _.get(_.find(sizes, { gtin }), 'stock.selectedAmount', 0);
        const getPrepackAmountFromCart = (_id: string) => _.get(_.find(prepacks, { _id }), 'stock.selectedAmount', 0);
        const getSelectedDeliveryDate = (styleGroupId: string, colorGroupId: string) =>
            _.get(
                _.find(
                    items,
                    (article: PreorderArticle) =>
                        article.styleGroupId === styleGroupId && article.color.colorGroupId === colorGroupId
                ),
                'selectedDeliveryDate',
                null
            );

        return _.map(articles, (article: ReorderArticle | PreorderArticle) => ({
            ...article,
            selectedDeliveryDate:
                getSelectedDeliveryDate(article.styleGroupId, article.color.colorGroupId) ||
                (article as PreorderArticle).selectedDeliveryDate,
            sizes: _.map(article.sizes, (size: ReorderArticleSize | PreorderArticleSize) => ({
                ...size,
                stock: { ...size.stock, selectedAmount: getSizeAmountFromCart(size.gtin) }
            })),
            prepacks: _.map(article.prepacks, (prepack: ReorderPrepack | PrepackForPreorder) => ({
                ...prepack,
                stock: { ...prepack.stock, selectedAmount: getPrepackAmountFromCart(prepack._id) }
            }))
        }));
    }
}

type OrderArticles = PreorderArticle[] | ReorderArticle[];

type OrderArticlesWithColors = {
    articles: OrderArticles;
    colors: OrderArticleColor[];
};

export enum OrderProduct {
    Reorder = 'reorder',
    Preorder = 'preorder'
}

export interface ArticleMapConfig {
    placeholderVariant?: PlaceholderVariant;
    prepackLabelPrefix: string;
    isNewMatrixEnabled?: boolean;
    availableText?: string;
    multiWeekBackorderText?: string;
    priceWarningText?: string;
    productType?: OrderProduct;
    userLanguage?: string;
    showAvailableOnly?: boolean;
    isShoppingCart?: boolean;
    stores?: Branch[];
    showStoreStock?: boolean;
    labels?: {
        [key in MatrixLabels]: string;
    };
    selectedQuantityMap?: { [gtin: string]: number };
}

export enum MatrixLabels {
    Prepack = 'ORDER.ORDER_MATRIX.PREPACK',
    AvailableStock = 'ORDER.ORDER_MATRIX.AVAILABLE_TEXT',
    BackorderMultiWeek = 'ORDER.ORDER_MATRIX.BACKORDER_TEXT_MULTI_WEEK',
    BackorderMultiWeekSingular = 'ORDER.ORDER_MATRIX.BACKORDER_TEXT_MULTI_WEEK_SINGULAR',
    PriceWarning = 'ORDER.ORDER_MATRIX.PRICE_WARNING',
    InventoryAvailable = 'ORDER.ORDER_MATRIX.INVENTORY_AVAILABLE',
    InventoryUnavailable = 'ORDER.ORDER_MATRIX.INVENTORY_UNAVAILABLE'
}

type MatrixObservableChain = [(PreorderArticle | ReorderArticle)[], TranslationMap, boolean, Branch[], boolean];
